/**
 * Buscador global Ctrl+K — obras, facturas, pagos, avales y empleados.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Search, HardHat, ReceiptEuro, FileText, CreditCard, ShieldCheck, User } from "lucide-react";

const TIPO = {
  obra: { label: "Obras", Icon: HardHat },
  factura: { label: "Facturas emitidas", Icon: ReceiptEuro },
  factura_recibida: { label: "Facturas recibidas", Icon: FileText },
  pago: { label: "Pagos", Icon: CreditCard },
  aval: { label: "Avales", Icon: ShieldCheck },
  empleado: { label: "Empleados", Icon: User },
};

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setItems([]); return; }
    setLoading(true);
    timer.current = setTimeout(() => {
      api.get(`/buscar?q=${encodeURIComponent(q.trim())}`)
        .then(r => setItems(r.data.items || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 300);
  }, [q]);

  const ir = (it) => {
    setOpen(false);
    setQ("");
    navigate(it.ruta);
  };

  const grupos = Object.keys(TIPO).map(t => ({ tipo: t, ...TIPO[t], items: items.filter(i => i.tipo === t) })).filter(g => g.items.length);

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-btn"
              title="Buscar en toda la app (Ctrl+K)"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full hover:bg-black/[0.05] dark:hover:bg-white/[0.06] text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)] transition-colors text-xs font-bold">
        <Search className="h-4 w-4" />
        <span className="hidden md:inline">Buscar</span>
        <kbd className="hidden md:inline text-[9px] font-black px-1 py-0.5 rounded border border-[var(--legatum-border)]">Ctrl K</kbd>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 max-w-lg" aria-describedby={undefined}>
          <Command shouldFilter={false}>
            <CommandInput placeholder="Busca obras, facturas, pagos, avales, empleados..." value={q} onValueChange={setQ} data-testid="global-search-input" />
            <CommandList data-testid="global-search-results">
              <CommandEmpty>{loading ? "Buscando..." : q.trim().length < 2 ? "Escribe al menos 2 caracteres" : "Sin resultados"}</CommandEmpty>
              {grupos.map(g => (
                <CommandGroup key={g.tipo} heading={g.label}>
                  {g.items.map(it => (
                    <CommandItem key={`${g.tipo}-${it.id}`} value={`${g.tipo}-${it.id}`} onSelect={() => ir(it)} data-testid={`search-result-${g.tipo}-${it.id}`}>
                      <g.Icon className="h-3.5 w-3.5 mr-2 shrink-0 opacity-60" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold truncate">{it.titulo}</div>
                        {it.subtitulo && <div className="text-[10px] text-[var(--legatum-text-muted)] truncate">{it.subtitulo}</div>}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
};
